import { useTranslation } from "react-i18next";
import { FC, Props } from "./typings";
import styles from "./styles.module.scss";
import useCustomQuery from "hooks/useCustomQuery";
import { axiosInstance } from "api/axios";
import Loading from "components/Loading";
import moment from "moment";
import classNames from "classnames";
import { IWorkspaceKeyHealthResponse } from "dto/Admin/IWorkspaceKeyHealth";

/**
 * Faza 8 modelu kluczy firmowych (docs/model-kluczy-firmowych.md) — dawniej panel admina
 * nie widział ani firm, ani kluczy. Ten ekran odpowiada na cztery pytania z tej fazy: kto
 * nie ma firmy, kto nie ma grantu klucza, która generacja jest aktywna, u kogo zawiodło
 * wydanie klucza.
 */
const WorkspaceKeyHealth: FC<Props> = ({ searchText }) => {
  const { t } = useTranslation();

  const { data, isLoading } = useCustomQuery<IWorkspaceKeyHealthResponse>(
    ["admin-workspace-key-health"],
    async () => {
      const res = await axiosInstance.get("/admin/workspaceKeyHealth");
      return res.data;
    },
  );

  if (isLoading || !data) {
    return <Loading />;
  }

  const search = searchText.trim().toLowerCase();
  const rows = search
    ? data.Workspaces.filter((w) =>
        (w.WorkspaceName ?? "").toLowerCase().includes(search),
      )
    : data.Workspaces;

  return (
    <div className={styles.wrapper}>
      <div className={styles.summary}>
        <div className={styles.stat}>
          <div className={styles.value}>{data.Workspaces.length}</div>
          <div className={styles.label}>
            {t("admin.workspaceKeyHealth.workspaces", "Firmy")}
          </div>
        </div>
        <div className={styles.stat}>
          <div className={styles.value}>{data.UsersWithoutWorkspaceCount}</div>
          <div className={styles.label}>
            {t(
              "admin.workspaceKeyHealth.usersWithoutWorkspace",
              "Konta bez firmy",
            )}
          </div>
        </div>
        <div className={styles.stat}>
          <div className={styles.value}>
            {data.Workspaces.filter((w) => !w.ActiveWorkspaceKeyId).length}
          </div>
          <div className={styles.label}>
            {t(
              "admin.workspaceKeyHealth.workspacesWithoutKey",
              "Firmy bez klucza",
            )}
          </div>
        </div>
        <div className={styles.stat}>
          <div className={styles.value}>
            {data.Workspaces.reduce(
              (sum, w) => sum + w.MembersMissingActiveKeyGrantCount,
              0,
            )}
          </div>
          <div className={styles.label}>
            {t(
              "admin.workspaceKeyHealth.membersMissingGrant",
              "Członkowie bez grantu",
            )}
          </div>
        </div>
      </div>

      <table className={styles.table}>
        <thead>
          <tr>
            <th>{t("admin.workspaceKeyHealth.name", "Firma")}</th>
            <th>
              {t("admin.workspaceKeyHealth.members", "Aktywni członkowie")}
            </th>
            <th>
              {t("admin.workspaceKeyHealth.generation", "Aktywna generacja")}
            </th>
            <th>
              {t(
                "admin.workspaceKeyHealth.generations",
                "Wszystkich generacji",
              )}
            </th>
            <th>
              {t("admin.workspaceKeyHealth.missing", "Bez grantu klucza")}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((w) => (
            <tr key={w.WorkspaceId}>
              <td>
                {w.WorkspaceName || w.WorkspaceId}
                {w.IsPlatform && (
                  <span className={classNames(styles.badge, styles.warn)}>
                    {" "}
                    platform
                  </span>
                )}
              </td>
              <td>{w.ActiveMembersCount}</td>
              <td>
                {w.ActiveWorkspaceKeyId ? (
                  <span className={classNames(styles.badge, styles.ok)}>
                    gen. {w.ActiveGeneration}
                    {w.ActiveKeyCreationDate
                      ? ` · ${moment(w.ActiveKeyCreationDate).format(
                          "DD.MM.YY",
                        )}`
                      : ""}
                  </span>
                ) : (
                  <span className={classNames(styles.badge, styles.error)}>
                    {t("admin.workspaceKeyHealth.noKey", "brak klucza")}
                  </span>
                )}
              </td>
              <td>{w.WorkspaceKeyGenerationsCount}</td>
              <td>
                {w.MembersMissingActiveKeyGrantCount > 0 ? (
                  <>
                    <span className={classNames(styles.badge, styles.error)}>
                      {w.MembersMissingActiveKeyGrantCount}
                    </span>
                    <div className={styles.missingLabels}>
                      {w.MembersMissingActiveKeyGrantLabels.join(", ")}
                    </div>
                  </>
                ) : (
                  <span className={classNames(styles.badge, styles.ok)}>0</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default WorkspaceKeyHealth;
